import { apiService } from "../../../shared/services/apiService";

// Interfaces baseadas na documentação OpenAPI
export interface ProdutoBanner {
    codigoBarras?: string;
    produtoId: number;
    nome?: string;
    vlPreco?: number;
    vlAtacadoNivel1?: number;
    qtAtacadoNivel1?: number;
    vlPrecoNormal?: number;
    promocao?: boolean;
    vlAtacadoNivel2?: number;
    qtAtacadoNivel2?: number;
    vlPrecoClienteFidelizado?: number;
    qtMinimaPrecoClienteFidelizado?: number;
    tipoCartaoPrecoClienteFidelizado?: number;
    campanhaId?: number;
    campanha?: string;
    dtFinalCampanha?: string;
    qtMaximaPorVenda?: number;
    tipoCampanha?: string;
    percentualCashBack?: number;
    tipoUnidade?: string;
    dtPrecoAlterado?: string;
    /** Seção/prateleira — GET da tela pode trazer id e/ou nome da categoria */
    secaoId?: number;
    nomeSecao?: string;
    secaoNome?: string;
    nomeCategoria?: string;
    categoriaNome?: string;
    categoria?: string;
    descricaoSecao?: string;
    tag?: string;
    dtInicialCampanha?: string;
    dtInicioCampanha?: string;
}

/**
 * Nome exibível da categoria/seção a partir do objeto produto do GET banner/tela.
 * Não usa `campanha` (é nome da campanha de preço, não da prateleira).
 */
export function resolveProdutoBannerCategoriaNome(
    produto: ProdutoBanner & Record<string, unknown>
): string {
    const p = produto as Record<string, unknown>;
    const strings = [
        p.nomeSecao,
        p.NomeSecao,
        p.secaoNome,
        p.SecaoNome,
        p.nomeCategoria,
        p.NomeCategoria,
        p.categoriaNome,
        p.CategoriaNome,
        p.categoria,
        p.Categoria,
        p.descricaoSecao,
        p.DescricaoSecao,
        p.tag,
        p.Tag,
    ];
    for (const c of strings) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    const secaoObj = p.secao;
    if (secaoObj && typeof secaoObj === 'object' && !Array.isArray(secaoObj)) {
        const o = secaoObj as Record<string, unknown>;
        const nested = o.nome ?? o.descricao ?? o.nomeSecao;
        if (typeof nested === 'string' && nested.trim()) return nested.trim();
    }
    return '';
}

export interface TelaBanner {
    id?: number;
    nome: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    produtoIds?: ProdutoBanner[]; // Array de produtos vinculados (retornado pelo GET)
    dataCriacao?: string;
    dataAtualizacao?: string;
    dataHora?: string;
    video?: boolean;
    configuracoes?: string;
}

/**
 * Opções para processamento seletivo de campos do JSON de configuracoes
 * Permite especificar quais campos processar e quais manter como JSON
 */
export interface ProcessamentoConfiguracoes {
    // Campos a serem extraídos e processados (serão adicionados como propriedades diretas)
    camposParaProcessar?: string[]; // Ex: ['descricao', 'tag', 'categoria']
    // Se true, mantém o JSON completo parseado em _configParsed
    manterJsonParseado?: boolean;
}

/**
 * Interface otimizada para listagem de telas com processamento seletivo
 * Retorna TODOS os campos da tela (completo)
 * MAS processa apenas os campos especificados em ProcessamentoConfiguracoes
 * O resto do JSON fica disponível mas não processado
 */
export interface TelaBannerComProcessamentoSeletivo extends TelaBanner {
    // Campos processados dinamicamente (baseado em camposParaProcessar)
    // Ex: se camposParaProcessar = ['descricao', 'tag']
    //     teremos: descricaoExtraida, tagExtraida
    [key: string]: any; // Permite adicionar campos processados dinamicamente
    // JSON parseado mantido em cache (se manterJsonParseado = true)
    _configParsed?: any; // Cache do JSON parseado para uso futuro
}

export interface InserirTelaBannerCommand {
    nome: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    produtoIds?: number[]; // Array de IDs dos produtos a vincular
    configuracoes?: string;
}

export interface AlterarTelaBannerCommand {
    id: number;
    nome?: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    produtoIds?: number[] | null; // Array de IDs dos produtos a vincular (null quando não houver produtos)
    configuracoes?: string;
}

export interface ListarMinhasTelasBannerQueryResultPackedResult {
    data: TelaBanner[];
    totalCount: number;
}

export interface InserirTelaBannerCommandResult {
    id?: number;
    telaId?: number; // A API pode retornar telaId ao invés de id
    nome: string;
    descricao?: string;
    ativo: boolean;
}

export interface CodigoResponse {
    codigo: string;
    dataCriacao: string;
}

/**
 * Interface otimizada para listagem de telas - contém todos os campos necessários
 * mas com produtos apenas como IDs (não objetos completos) para reduzir o tamanho da resposta
 * 
 * IMPORTANTE: Inclui configuracoes (layoutStyle) para evitar buscas adicionais
 */
export interface TelaBannerOtimizada {
    id: number;
    nome: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    produtoIds: number[]; // Apenas IDs dos produtos, não os objetos completos
    configuracoes?: string; // JSON string com layoutStyle - INCLUÍDO para evitar buscas adicionais
    dataCriacao?: string;
    dataAtualizacao?: string;
    dataHora?: string;
    video?: boolean;
}

/**
 * Interface otimizada para produtos vinculados a uma tela
 * Contém apenas os campos necessários para exibição no modal de produtos
 */
export interface ProdutoBannerOtimizado {
    produtoId: number;
    nome?: string;
    vlPreco?: number;
    vlPrecoNormal?: number;
    tipoUnidade?: string;
    codigoBarras?: string;
}

/**
 * Interface ULTRA-OTIMIZADA para listagem básica de telas
 * Retorna APENAS os campos essenciais para exibição nos cards
 * SEM configuracoes, SEM produtos, SEM nada pesado
 * 
 * Use este para listagem inicial no DisplayPage
 */
export interface TelaBannerBasica {
    id: number;
    nome: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    // NÃO inclui: configuracoes, produtoIds, video, dataCriacao, etc.
}

/**
 * Interface para televisão - retorna JSON bruto mas SEM processar
 * Não baixa imagens/vídeos, não processa layoutStyle
 * Apenas mantém o JSON de configuracoes como string bruta
 */
export interface TelaBannerParaTelevisao {
    id: number;
    nome: string;
    descricao?: string;
    tipo?: number;
    ativo?: boolean;
    configuracoes?: string; // JSON bruto, NÃO processado
    video?: boolean;
    // NÃO inclui: produtoIds completos (apenas IDs se necessário)
    produtoIds?: number[]; // Apenas IDs, não objetos completos
}

/**
 * Interface mínima para busca inicial (apenas id e nome)
 * Usado para listagem rápida na página de televisões
 */
export interface TelaMinima {
    id: number;
    nome: string;
}

/**
 * Interface para televisão (id, nome, tipo)
 * Usado para vincular telas a televisões
 */
export interface TelaParaTelevisao {
    id: number;
    nome: string;
    tipo?: number;
}

/**
 * Interface otimizada para listagem na página de Telas (Screens)
 * Retorna: id, nome, tipo, descricao, categoria (tag), produtoIds
 * Usa endpoints personalizados para otimizar a busca
 */
export interface TelaBannerParaScreens {
    id: number;
    nome: string;
    tipo?: number;
    ativo?: boolean;
    descricao?: string; // Extraída do JSON de configuracoes
    categoria?: string; // Extraída do JSON de configuracoes (campo "tag")
    produtoIds?: number[]; // Apenas IDs dos produtos
}

/**
 * Interface otimizada para listagem na página de Style Editor
 * Retorna: id, nome, tipo, ativo, categoria (tag), configuracoes
 * Usa endpoints personalizados para otimizar a busca (sem produtos)
 */
export interface TelaBannerParaStyleEditor {
    id: number;
    nome: string;
    tipo?: number;
    ativo?: boolean;
    categoria?: string; // Extraída do JSON de configuracoes (campo "tag")
    configuracoes?: string; // JSON completo para layoutStyle
}

/**
 * Interface otimizada para listagem na página de Vídeos
 * Retorna: id, nome, tipo, ativo, video (flag indicando se tem vídeo)
 * Usa endpoints personalizados para otimizar a busca (sem produtos)
 */
export interface TelaBannerParaVideo {
    id: number;
    nome: string;
    tipo?: number;
    ativo?: boolean;
    video?: boolean; // Flag indicando se a tela tem vídeo
}

export interface TelaFotoPayload {
    cdTela: number;
    fotoBase64: string;
}

class BannerService {
    private telaFotoBlobUrlCache = new Map<number, string>();
    private invalidateTelaFotoCache(cdTela: number) {
        const url = this.telaFotoBlobUrlCache.get(cdTela);
        if (url) {
            try { URL.revokeObjectURL(url); } catch {}
        }
        this.telaFotoBlobUrlCache.delete(cdTela);
    }
    /** Invalidação pública para fluxos externos (ex.: atualizações via SignalR). */
    public invalidarCacheFotoTela(cdTela: number): void {
        this.invalidateTelaFotoCache(cdTela);
    }
    /**
     * Lista as telas de banner da empresa/banco de dados configurado no login
     * As telas são compartilhadas entre todos os usuários da mesma empresa
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelas(id?: number, noCache?: boolean): Promise<TelaBanner[]> {
        const params = id ? `?Id=${id}` : '';
        // CRÍTICO: Usar cache: 'no-store' para garantir dados sempre frescos
        return apiService.get<TelaBanner[]>(`/v1/banner/tela${params}`, noCache ? { cache: 'no-store' } : undefined);
    }

    /**
     * Lista telas com processamento seletivo de campos
     * Retorna TODOS os dados completos da API (produtoIds, configuracoes, etc.)
     * MAS processa apenas os campos especificados do JSON de configuracoes
     * O resto fica como JSON string disponível para uso futuro
     * 
     * EXEMPLO DE USO:
     * ```typescript
     * // Processar apenas descricao e tag, manter resto como JSON
     * const telas = await bannerService.listarTelasComProcessamentoSeletivo({
     *   camposParaProcessar: ['descricao', 'tag'],
     *   manterJsonParseado: true
     * });
     * // telas[0].descricaoExtraida - campo processado
     * // telas[0].configuracoes - JSON completo disponível
     * // telas[0]._configParsed - JSON parseado em cache
     * ```
     * 
     * OTIMIZAÇÃO:
     * - Faz parse do JSON apenas uma vez por tela
     * - Extrai apenas os campos especificados (não processa layoutStyle completo)
     * - Mantém produtoIds completos disponíveis (mas não processa)
     * - Mantém configuracoes completo como JSON string
     * - Opcionalmente mantém JSON parseado em cache (_configParsed)
     * 
     * @param opcoes - Opções de processamento seletivo
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasComProcessamentoSeletivo(
        opcoes: ProcessamentoConfiguracoes = {},
        id?: number
    ): Promise<TelaBannerComProcessamentoSeletivo[]> {
        const params = id ? `?Id=${id}` : '';
        const telas = await apiService.get<TelaBanner[]>(`/v1/banner/tela${params}`);
        
        const { camposParaProcessar = [], manterJsonParseado = false } = opcoes;
        
        return telas.map(tela => {
            if (!tela.id) {
                return null as any;
            }

            // Criar objeto base com TODOS os campos da tela (completo)
            const telaOtimizada: TelaBannerComProcessamentoSeletivo = {
                ...tela,
                id: tela.id
            } as TelaBannerComProcessamentoSeletivo;

            // Processar JSON de configuracoes apenas se houver campos para processar
            if (tela.configuracoes && 
                tela.configuracoes.trim() !== '' && 
                tela.configuracoes.trim() !== 'string' && 
                tela.configuracoes.trim() !== '"string"') {
                try {
                    // Parse uma vez
                    const configParsed = JSON.parse(tela.configuracoes);
                    
                    // Se solicitado, manter JSON parseado em cache
                    if (manterJsonParseado) {
                        telaOtimizada._configParsed = configParsed;
                    }
                    
                    // Processar apenas os campos especificados
                    // IMPORTANTE: Campos NÃO especificados (ex: backgroundImage, productContainer, etc.)
                    //            ficam apenas no JSON string configuracoes e NÃO são processados
                    //            Isso evita processar imagens base64 grandes, estilos completos, etc.
                    if (camposParaProcessar.length > 0) {
                        camposParaProcessar.forEach(campo => {
                            const valor = configParsed?.[campo];
                            if (valor !== undefined && valor !== null) {
                                // Adicionar campo processado com sufixo "Extraida" para evitar conflitos
                                const nomeCampoProcessado = `${campo}Extraida`;
                                telaOtimizada[nomeCampoProcessado] = typeof valor === 'string' 
                                    ? valor.trim() 
                                    : valor;
                            }
                        });
                    }
                    // NOTA: Se você precisar acessar outros campos depois (ex: backgroundImage),
                    //       eles estarão disponíveis em:
                    //       - telaOtimizada.configuracoes (JSON string)
                    //       - telaOtimizada._configParsed (objeto parseado, se manterJsonParseado = true)
                } catch (error) {
                    // Erro ao parsear configurações
                }
            }

            return telaOtimizada;
        }).filter(tela => tela !== null);
    }

    /**
     * Método de conveniência para listagem na tela de televisão
     * Processa apenas descricao e tag (categoria) do JSON de configuracoes
     * Mantém todo o resto disponível mas não processado
     * 
     * @deprecated Use listarTelasParaTelevisaoBasico() para melhor performance
     */
    async listarTelasParaTelevisao(id?: number): Promise<TelaBannerComProcessamentoSeletivo[]> {
        return this.listarTelasComProcessamentoSeletivo({
            camposParaProcessar: ['descricao', 'tag'],
            manterJsonParseado: true
        }, id);
    }

    /**
     * OTIMIZAÇÃO CRÍTICA: Lista telas para televisão SEM processar configuracoes
     * Retorna apenas JSON bruto de configuracoes (não processa, não baixa imagens/vídeos)
     * 
     * Para televisão, não precisamos interpretar vídeos e imagens.
     * Apenas carregamos o JSON mas não baixamos nem processamos nada.
     * Só será inserido telas na televisão, não será customizado nem editado.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasParaTelevisaoBasico(id?: number): Promise<TelaBannerParaTelevisao[]> {
        const params = id ? `?Id=${id}` : '';
        const telas = await apiService.get<TelaBanner[]>(`/v1/banner/tela${params}`);
        
        // Retornar apenas o necessário: id, nome, descricao, tipo, ativo, configuracoes (JSON bruto)
        // Extrair apenas IDs dos produtos (não objetos completos)
        return telas
            .filter(tela => tela.id != null)
            .map(tela => {
                // Extrair apenas IDs dos produtos de forma eficiente
                let produtoIds: number[] = [];
                if (Array.isArray(tela.produtoIds) && tela.produtoIds.length > 0) {
                    produtoIds = tela.produtoIds
                        .map(p => p?.produtoId)
                        .filter((id): id is number => typeof id === 'number' && id > 0);
                }

                return {
                    id: tela.id!,
                    nome: tela.nome,
                    descricao: tela.descricao,
                    tipo: tela.tipo,
                    ativo: tela.ativo,
                    configuracoes: tela.configuracoes, // JSON bruto, NÃO processado
                    video: tela.video,
                    produtoIds: produtoIds.length > 0 ? produtoIds : undefined
                } as TelaBannerParaTelevisao;
            });
    }

    /**
     * Método de conveniência para listagem rápida na tela de visualização
     * Retorna TODOS os dados da API, mas processa APENAS descricao do JSON de configuracoes
     * 
     * O que é exibido nos cards:
     * - nome (já vem da API)
     * - id (já vem da API)
     * - tipo (já vem da API, convertido para layoutName)
     * - descricao (extraída do JSON de configuracoes - campo 'descricao')
     * 
     * NÃO processa: layoutStyle completo, imagens base64, produtos, tag, etc.
     * 
     * Use este método para listagem rápida. Quando o usuário clicar em uma tela,
     * use listarTelas(id) para carregar e interpretar TODOS os dados completos.
     * 
     * @deprecated Use listarTelasBasico() para melhor performance (não carrega configuracoes)
     */
    async listarTelasParaVisualizacao(id?: number): Promise<TelaBannerComProcessamentoSeletivo[]> {
        // Para visualização, processar APENAS descricao do JSON
        // Ignorar todo o resto (layoutStyle, imagens, etc.)
        return this.listarTelasComProcessamentoSeletivo({
            camposParaProcessar: ['descricao'], // Apenas descricao - resto ignorado
            manterJsonParseado: false // Não precisa manter parseado
        }, id);
    }

    /**
     * OTIMIZAÇÃO CRÍTICA: Lista telas básicas para exibição nos cards
     * Retorna APENAS: id, nome, descricao, tipo
     * 
     * NÃO retorna: configuracoes (layoutStyle), produtos, imagens, vídeos, etc.
     * 
     * O estilo só precisa ser carregado quando visualizar uma tela específica.
     * Para o loading inicial, só precisamos das informações dos cards.
     * 
     * Isso reduz drasticamente o tamanho da resposta da API (sem imagens base64, sem layoutStyle completo).
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasBasico(id?: number): Promise<TelaBannerBasica[]> {
        const params = id ? `?Id=${id}` : '';
        const telas = await apiService.get<TelaBanner[]>(`/v1/banner/tela${params}`);
        
        // Retornar APENAS os campos essenciais para os cards
        // Remover completamente: configuracoes, produtoIds, video, dataCriacao, etc.
        return telas
            .filter(tela => tela.id != null)
            .map(tela => ({
                id: tela.id!,
                nome: tela.nome,
                descricao: tela.descricao,
                tipo: tela.tipo,
                ativo: tela.ativo
            } as TelaBannerBasica));
    }

    /**
     * OTIMIZAÇÃO: Lista telas otimizado para página de Display
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - produtos=N: não retorna produtos
     * - descricao=S: retorna descrição (campo específico do JSON)
     * - NÃO passa configuracoes=S (isso retornaria TODAS as configurações)
     * 
     * Retorna: id, nome, descricao, tipo, ativo
     * 
     * A descrição pode vir diretamente no campo descricao OU dentro de configuracoes.
     * Este método processa ambos os casos.
     * 
     * Use este método na página de Display para listagem inicial de telas.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoParaDisplay(id?: number): Promise<TelaBannerBasica[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            produtos: 'N'      // Não retornar produtos
        });
        
        // Adicionar descricao (retornar apenas descrição - campo específico do JSON)
        params.append('descricao', 'S');
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        // NÃO passar configuracoes=S - isso retornaria TODAS as configurações
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Filtrar apenas campos necessários
        const result: TelaBannerBasica[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id != null) {
                // A descrição pode vir diretamente no campo descricao OU dentro de configuracoes
                let descricao = tela.descricao ? String(tela.descricao).trim() : undefined;
                
                // Se não tiver descricao direta, extrair do JSON de configuracoes
                if (!descricao && tela.configuracoes) {
                    try {
                        const config = typeof tela.configuracoes === 'string' 
                            ? JSON.parse(tela.configuracoes) 
                            : tela.configuracoes;
                        
                        if (config && config.descricao) {
                            descricao = String(config.descricao).trim();
                        }
                    } catch (e) {
                        // Ignorar erro de parse
                        console.warn(`[bannerService] Erro ao parsear configuracoes da tela ${tela.id}:`, e);
                    }
                }
                
                result.push({
                    id: tela.id,
                    nome: tela.nome || '',
                    descricao: descricao || undefined, // Garantir que seja undefined se vazio
                    tipo: tela.tipo,
                    ativo: tela.ativo
                });
            }
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas MÍNIMO - apenas id e nome (para busca inicial na página de televisões)
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - produtos=N: não retorna produtos
     * - NÃO passa descricao=S (não precisa de descrição)
     * - NÃO passa configuracoes=S (isso retornaria TODAS as configurações)
     * 
     * Retorna: apenas id e nome
     * 
     * Usado quando entra na página de televisões para listar quais telas pertencem àquela televisão.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasMinimo(id?: number): Promise<TelaMinima[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            produtos: 'N'      // Não retornar produtos
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        // NÃO passar descricao=S (não precisa de descrição)
        // NÃO passar configuracoes=S (isso retornaria TODAS as configurações)
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Filtrar apenas id e nome
        const result: TelaMinima[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id != null) {
                result.push({
                    id: tela.id,
                    nome: tela.nome || ''
                });
            }
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas para televisão - id, nome e tipo (para quando clicar em gerenciar)
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - produtos=N: não retorna produtos
     * - NÃO passa descricao=S (não precisa de descrição)
     * - NÃO passa configuracoes=S (isso retornaria TODAS as configurações)
     * 
     * Retorna: id, nome, tipo
     * 
     * Usado quando clica em "gerenciar" na televisão para vincular telas.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoParaTelevisao(id?: number): Promise<TelaParaTelevisao[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            produtos: 'N'      // Não retornar produtos
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        // NÃO passar descricao=S (não precisa de descrição)
        // NÃO passar configuracoes=S (isso retornaria TODAS as configurações)
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Filtrar apenas id, nome e tipo
        const result: TelaParaTelevisao[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id != null) {
                result.push({
                    id: tela.id,
                    nome: tela.nome || '',
                    tipo: tela.tipo
                });
            }
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas COM produtos - para vincular produtos
     * 
     * Usa parâmetros da API para retornar telas COM produtos:
     * - Produtos=S: retorna produtos vinculados às telas
     * - NÃO passa configuracoes=S (isso retornaria TODAS as configurações)
     * 
     * Retorna: id, nome, descricao, tipo, ativo, configuracoes (JSON bruto), produtoIds
     * 
     * Use este método quando precisar listar telas para vincular produtos.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoComProdutos(id?: number): Promise<TelaBannerOtimizada[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            Produtos: 'S'      // Retornar produtos (para vincular produtos)
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        // NÃO passar configuracoes=S - isso retornaria TODAS as configurações
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telas = await apiService.get<TelaBanner[]>(endpoint);
        
        // Transformar para retornar todos os campos, COM produtos (apenas IDs)
        const telasLength = telas.length;
        const result: TelaBannerOtimizada[] = new Array(telasLength);
        
        for (let i = 0; i < telasLength; i++) {
            const tela = telas[i];
            
            // Extrair IDs dos produtos de forma ultra-eficiente
            let produtoIds: number[] = [];
            const produtoIdsArray = tela.produtoIds;
            if (Array.isArray(produtoIdsArray) && produtoIdsArray.length > 0) {
                const produtoIdsLength = produtoIdsArray.length;
                produtoIds = new Array(produtoIdsLength);
                let idx = 0;
                for (let j = 0; j < produtoIdsLength; j++) {
                    const produto = produtoIdsArray[j];
                    const produtoId = produto?.produtoId;
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        produtoIds[idx++] = produtoId;
                    }
                }
                if (idx < produtoIdsLength) {
                    produtoIds = produtoIds.slice(0, idx);
                }
            }
            
            // IMPORTANTE: Incluir TODOS os campos, incluindo configuracoes e produtoIds
            result[i] = {
                id: tela.id!,
                nome: tela.nome,
                descricao: tela.descricao,
                tipo: tela.tipo,
                ativo: tela.ativo,
                produtoIds: produtoIds, // COM produtos (apenas IDs)
                configuracoes: tela.configuracoes, // INCLUÍDO para evitar buscas adicionais
                dataCriacao: tela.dataCriacao,
                dataAtualizacao: tela.dataAtualizacao,
                dataHora: tela.dataHora,
                video: tela.video
            };
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas para página de Screens (Telas)
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - descricao=S: retorna descrição (campo específico do JSON)
     * - produtos=S: retorna produtos vinculados às telas
     * - tag=S: retorna categoria (campo "tag" do JSON)
     * 
     * Retorna: id, nome, tipo, ativo, descricao, categoria (tag), produtoIds
     * 
     * Use este método na página de Screens para listagem inicial de telas.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoParaScreens(id?: number): Promise<TelaBannerParaScreens[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            descricao: 'S',      // Retornar descrição (campo específico do JSON)
            produtos: 'S',       // Retornar produtos
            tag: 'S'             // Retornar categoria (campo "tag" do JSON)
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Processar telas: extrair descrição, categoria (tag) e produtoIds
        const result: TelaBannerParaScreens[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id == null) {
                continue;
            }
            
            // Extrair descrição: primeiro do campo descricao, depois do JSON de configuracoes
            let descricao = tela.descricao ? String(tela.descricao).trim() : undefined;
            if (!descricao && tela.configuracoes) {
                try {
                    const config = typeof tela.configuracoes === 'string' 
                        ? JSON.parse(tela.configuracoes) 
                        : tela.configuracoes;
                    
                    // Tentar extrair descrição do JSON (pode estar em config.descricao ou config.description)
                    if (config?.descricao) {
                        descricao = String(config.descricao).trim();
                    } else if (config?.description) {
                        // Se for um objeto description, extrair o texto se houver
                        if (typeof config.description === 'string') {
                            descricao = config.description.trim();
                        }
                    }
                } catch (e) {
                    // Ignorar erro de parse
                    console.warn(`[bannerService] Erro ao parsear configuracoes da tela ${tela.id}:`, e);
                }
            }
            
            // Extrair categoria (tag) do JSON de configuracoes
            let categoria: string | undefined = undefined;
            if (tela.configuracoes) {
                try {
                    const config = typeof tela.configuracoes === 'string' 
                        ? JSON.parse(tela.configuracoes) 
                        : tela.configuracoes;
                    
                    if (config?.tag && typeof config.tag === 'string') {
                        categoria = config.tag.trim().toUpperCase();
                    }
                } catch (e) {
                    // Ignorar erro de parse
                    console.warn(`[bannerService] Erro ao parsear tag da tela ${tela.id}:`, e);
                }
            }
            
            // Extrair apenas IDs dos produtos
            let produtoIds: number[] = [];
            if (Array.isArray(tela.produtoIds) && tela.produtoIds.length > 0) {
                produtoIds = tela.produtoIds
                    .map(p => p?.produtoId)
                    .filter((id): id is number => typeof id === 'number' && id > 0);
            }
            
            result.push({
                id: tela.id,
                nome: tela.nome || '',
                tipo: tela.tipo,
                ativo: tela.ativo,
                descricao: descricao || undefined,
                categoria: categoria || undefined,
                produtoIds: produtoIds.length > 0 ? produtoIds : undefined
            });
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas para página de Vídeos
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - produtos=N: não retorna produtos (não precisa para listagem)
     * 
     * Retorna: id, nome, tipo, ativo, video (flag indicando se tem vídeo)
     * 
     * Use este método na página de Vídeos para listagem inicial de telas.
     * O campo `video` (true/false) indica se a tela já tem vídeo.
     * Quando uma tela for selecionada e tiver `video === true`, carregue o vídeo atual.
     * Se `video === false` ou `undefined`, a pessoa pode fazer upload de um novo vídeo.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoParaVideo(id?: number): Promise<TelaBannerParaVideo[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            produtos: 'N'      // Não retornar produtos (não precisa para listagem)
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Processar telas: extrair apenas campos necessários
        const result: TelaBannerParaVideo[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id == null) {
                continue;
            }
            
            result.push({
                id: tela.id,
                nome: tela.nome || '',
                tipo: tela.tipo,
                ativo: tela.ativo,
                video: tela.video // Flag indicando se tem vídeo (true/false)
            });
        }
        
        return result;
    }

    /**
     * OTIMIZAÇÃO: Lista telas para página de Style Editor
     * 
     * Usa parâmetros da API para retornar apenas campos necessários:
     * - produtos=N: não retorna produtos (não precisa para listagem)
     * - configuracoes=S: retorna configurações completas (JSON com layoutStyle e tag/categoria)
     * 
     * Retorna: id, nome, tipo, ativo, categoria (tag), configuracoes (para layoutStyle)
     * 
     * Use este método na página de Style Editor para listagem inicial de telas.
     * Quando uma tela for selecionada para edição, use listarTelasOtimizado() para dados completos.
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizadoParaStyleEditor(id?: number): Promise<TelaBannerParaStyleEditor[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            produtos: 'N',          // Não retornar produtos (não precisa para listagem)
            configuracoes: 'S'     // Retornar configurações completas (JSON com layoutStyle e tag/categoria)
        });
        
        // Adicionar Id se fornecido
        if (id) {
            params.append('Id', id.toString());
        }
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telasRaw = await apiService.get<TelaBanner[]>(endpoint);
        
        // Processar telas: extrair categoria (tag) e manter configuracoes
        const result: TelaBannerParaStyleEditor[] = [];
        
        for (let i = 0; i < telasRaw.length; i++) {
            const tela = telasRaw[i];
            if (tela.id == null) {
                continue;
            }
            
            // Extrair categoria (tag) do JSON de configuracoes
            let categoria: string | undefined = undefined;
            if (tela.configuracoes) {
                try {
                    const config = typeof tela.configuracoes === 'string' 
                        ? JSON.parse(tela.configuracoes) 
                        : tela.configuracoes;
                    
                    if (config?.tag && typeof config.tag === 'string') {
                        categoria = config.tag.trim().toUpperCase();
                    }
                } catch (e) {
                    // Ignorar erro de parse
                    console.warn(`[bannerService] Erro ao parsear tag da tela ${tela.id}:`, e);
                }
            }
            
            result.push({
                id: tela.id,
                nome: tela.nome || '',
                tipo: tela.tipo,
                ativo: tela.ativo,
                categoria: categoria || undefined,
                configuracoes: tela.configuracoes // Manter JSON completo para layoutStyle
            });
        }
        
        return result;
    }

    /**
     * Lista as telas de banner de forma otimizada - retorna todos os campos necessários
     * mas com produtos apenas como IDs (não objetos completos) para reduzir o tamanho da resposta
     * 
     * OTIMIZAÇÃO: 
     * - Extrai apenas os IDs dos produtos sem processar os objetos completos
     * - INCLUI configuracoes (layoutStyle) para evitar buscas adicionais quando selecionar uma tela
     * - Reduz drasticamente o tamanho da resposta quando há muitos produtos vinculados
     * 
     * @param id - ID opcional para filtrar uma tela específica
     */
    async listarTelasOtimizado(id?: number): Promise<TelaBannerOtimizada[]> {
        const params = id ? `?Id=${id}` : '';
        const telas = await apiService.get<TelaBanner[]>(`/v1/banner/tela${params}`);
        
        // Transformar para retornar todos os campos, mas produtos apenas como IDs
        // OTIMIZAÇÃO CRÍTICA: Processar de forma ultra-rápida, evitando loops aninhados
        // Pré-alocar array de resultado para melhor performance
        const telasLength = telas.length;
        const result: TelaBannerOtimizada[] = new Array(telasLength);
        
        for (let i = 0; i < telasLength; i++) {
            const tela = telas[i];
            
            // Extrair IDs dos produtos de forma ultra-eficiente
            let produtoIds: number[] = [];
            const produtoIdsArray = tela.produtoIds;
            if (Array.isArray(produtoIdsArray) && produtoIdsArray.length > 0) {
                // Pré-alocar array com tamanho exato para melhor performance
                const produtoIdsLength = produtoIdsArray.length;
                produtoIds = new Array(produtoIdsLength);
                let idx = 0;
                for (let j = 0; j < produtoIdsLength; j++) {
                    const produto = produtoIdsArray[j];
                    const produtoId = produto?.produtoId;
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        produtoIds[idx++] = produtoId;
                    }
                }
                // Ajustar tamanho do array se necessário (só se houver diferença)
                if (idx < produtoIdsLength) {
                    produtoIds = produtoIds.slice(0, idx);
                }
            }

            // IMPORTANTE: Incluir TODOS os campos, incluindo configuracoes
            // Isso evita buscas adicionais quando uma tela é selecionada
            result[i] = {
                id: tela.id!,
                nome: tela.nome,
                descricao: tela.descricao,
                tipo: tela.tipo,
                ativo: tela.ativo,
                produtoIds: produtoIds, // Apenas IDs, não objetos completos
                configuracoes: tela.configuracoes, // INCLUÍDO para evitar buscas adicionais
                dataCriacao: tela.dataCriacao,
                dataAtualizacao: tela.dataAtualizacao,
                dataHora: tela.dataHora,
                video: tela.video
            };
        }
        
        return result;
    }

    /**
     * Busca os produtos vinculados a uma tela específica de forma otimizada
     * Retorna apenas os campos necessários para exibição no modal (id, nome, preço, unidade, codigoBarras)
     * Deve ser usado apenas quando necessário (ex: ao abrir o modal de produtos)
     * 
     * @deprecated Use buscarProdutosTelaOtimizadoComParametro() para melhor performance
     * @param telaId - ID da tela
     */
    async buscarProdutosTelaOtimizado(telaId: number): Promise<ProdutoBannerOtimizado[]> {
        const telas = await apiService.get<TelaBanner[]>(`/v1/banner/tela?Id=${telaId}`);
        const tela = telas.find(t => t.id === telaId);
        
        if (!tela || !tela.produtoIds || !Array.isArray(tela.produtoIds)) {
            return [];
        }

        // Retornar apenas os campos necessários para o modal
        return tela.produtoIds.map(produto => ({
            produtoId: produto.produtoId,
            nome: produto.nome,
            vlPreco: produto.vlPreco,
            vlPrecoNormal: produto.vlPrecoNormal,
            tipoUnidade: produto.tipoUnidade,
            codigoBarras: produto.codigoBarras
        }));
    }

    /**
     * OTIMIZAÇÃO: Busca produtos de uma tela usando parâmetro Produtos=S no endpoint
     * 
     * Usa parâmetros da API para retornar apenas produtos:
     * - Produtos=S: retorna produtos vinculados à tela
     * - Id={telaId}: filtra pela tela específica
     * 
     * Retorna apenas os campos necessários para exibição no modal:
     * - produtoId, nome, vlPreco, vlPrecoNormal, tipoUnidade, codigoBarras
     * 
     * Use este método quando precisar carregar produtos de uma tela específica.
     * 
     * @param telaId - ID da tela
     */
    async buscarProdutosTelaOtimizadoComParametro(telaId: number): Promise<ProdutoBannerOtimizado[]> {
        // Construir URL com parâmetros otimizados
        const params = new URLSearchParams({
            Id: telaId.toString(),
            Produtos: 'S'      // Retornar produtos
        });
        
        const endpoint = `/v1/banner/tela?${params.toString()}`;
        
        const telas = await apiService.get<TelaBanner[]>(endpoint);
        
        const tela = telas.find(t => t.id === telaId);
        
        if (!tela || !tela.produtoIds || !Array.isArray(tela.produtoIds)) {
            return [];
        }

        // Filtrar apenas os campos necessários para o modal
        const result = tela.produtoIds.map(produto => ({
            produtoId: produto.produtoId,
            nome: produto.nome,
            vlPreco: produto.vlPreco,
            vlPrecoNormal: produto.vlPrecoNormal,
            tipoUnidade: produto.tipoUnidade,
            codigoBarras: produto.codigoBarras
        } as ProdutoBannerOtimizado));
        
        return result;
    }

    /**
     * Busca preços atualizados de produtos específicos
     * Usado quando recebe evento ProdutoAtualizado via SignalR
     * 
     * IMPORTANTE: Usa POST ao invés de GET para evitar limite de URI
     * POST /v1/banner/precos com body: { "produtoIds": [405, 200, 300, ...] }
     * 
     * O delay e retry são feitos no handler do SignalR, não aqui
     * @param produtoIds - Array de IDs dos produtos para buscar preços atualizados
     * @returns Array de produtos com preços atualizados
     */
    async buscarPrecosAtualizados(produtoIds: number[]): Promise<ProdutoBanner[]> {
        if (!produtoIds || produtoIds.length === 0) {
            console.warn('[bannerService] ⚠️ Nenhum produtoId fornecido para buscar preços');
            return [];
        }

        try {
            // Usar POST para evitar limite de URI (pode enviar milhares de IDs)
            // Endpoint: POST /v1/banner/precos
            // Body: { "produtoIds": [405, 200, 300, 150, 789] }
            const endpoint = `/v1/banner/precos`;
            const body = {
                produtoIds: produtoIds
            };
            
            // Fazer POST com headers de cache-control para quebrar cache
            // O delay e retry são feitos no handler do SignalR (websocketService)
            const response = await apiService.post<any>(endpoint, body, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            
            // IMPORTANTE: A API retorna {hasNext: false, items: [...]}
            // Preciso extrair o array 'items' do objeto retornado
            let precos: ProdutoBanner[] = [];
            
            if (Array.isArray(response)) {
                // Se já for array direto (compatibilidade)
                precos = response;
            } else if (response && typeof response === 'object' && Array.isArray(response.items)) {
                // Se for objeto com 'items'
                precos = response.items;
            } else {
                console.warn(`[bannerService] ⚠️ Formato de resposta inválido`);
                return [];
            }
            
            return precos;
        } catch (error) {
            console.error('[bannerService] ❌ Erro ao buscar preços atualizados:', error);
            throw error;
        }
    }

    /**
     * Foto do produto para panfleto (Display / TV).
     * GET /v1/banner/produto/{produtoId}/foto — corpo binário (imagem).
     */
    async buscarFotoProduto(produtoId: number): Promise<Blob> {
        if (!produtoId || produtoId <= 0) {
            throw new Error('produtoId inválido para foto');
        }
        return apiService.downloadFile(`/v1/banner/produto/${produtoId}/foto`);
    }

    /** Foto de fundo da tela (panfleto). */
    /** Converte data URL ou base64 puro em File para upload multipart (campo `Foto`). */
    private fotoBase64ParaArquivo(fotoBase64: string): File {
        const trimmed = (fotoBase64 || '').trim();
        const dataUrl = trimmed.match(/^data:([^;]+);base64,(.+)$/);
        const mime = dataUrl ? dataUrl[1] : 'image/jpeg';
        const b64 = dataUrl ? dataUrl[2] : trimmed.replace(/\s/g, '');
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : 'jpg';
        return new File([bytes], `fundo-tela.${ext}`, { type: mime });
    }

    async buscarFotoTela(cdTela: number): Promise<string | null> {
        if (!cdTela || cdTela <= 0) return null;
        try {
            const cached = this.telaFotoBlobUrlCache.get(cdTela);
            if (cached) return cached;
            // Endpoint deve ser chamado exatamente como GET /v1/banner/tela/{id}/foto (sem query params extras).
            const blob = await apiService.downloadFile(`/v1/banner/tela/${cdTela}/foto`);
            if (!blob || blob.size === 0) return null;
            const objectUrl = URL.createObjectURL(blob);
            this.telaFotoBlobUrlCache.set(cdTela, objectUrl);
            return objectUrl;
        } catch {
            return null;
        }
    }

    /**
     * POST /v1/banner/tela/{cdTela}/foto — multipart, campo `Foto`.
     */
    async inserirFotoTela(payload: TelaFotoPayload): Promise<boolean> {
        const file = this.fotoBase64ParaArquivo(payload.fotoBase64);
        await apiService.uploadFile(`/v1/banner/tela/${payload.cdTela}/foto`, file, 'Foto');
        this.invalidateTelaFotoCache(payload.cdTela);
        return true;
    }

    /**
     * PUT /v1/banner/tela/{cdTela}/foto — multipart, campo `Foto`.
     */
    async atualizarFotoTela(payload: TelaFotoPayload): Promise<boolean> {
        const file = this.fotoBase64ParaArquivo(payload.fotoBase64);
        const formData = new FormData();
        formData.append('Foto', file);
        const ok = await apiService.putFormData<boolean>(`/v1/banner/tela/${payload.cdTela}/foto`, formData);
        this.invalidateTelaFotoCache(payload.cdTela);
        return typeof ok === 'boolean' ? ok : true;
    }

    async salvarFotoTela(cdTela: number, fotoBase64: string, hasExistingFoto: boolean): Promise<boolean> {
        if (hasExistingFoto) {
            return this.atualizarFotoTela({ cdTela, fotoBase64 });
        }
        return this.inserirFotoTela({ cdTela, fotoBase64 });
    }

    async deletarFotoTela(cdTela: number): Promise<boolean> {
        const ok = await apiService.delete<boolean>(`/v1/banner/tela/${cdTela}/foto`);
        this.invalidateTelaFotoCache(cdTela);
        return ok;
    }

    /**
     * Insere uma nova tela de banner para o usuário autenticado
     * @param command - Dados da tela a ser criada
     */
    async criarTela(command: InserirTelaBannerCommand): Promise<InserirTelaBannerCommandResult> {
        return apiService.post<InserirTelaBannerCommandResult>('/v1/banner/tela', command);
    }

    /**
     * Altera uma tela de banner do usuário autenticado
     * @param command - Dados da tela a ser alterada (deve incluir o ID)
     */
    async atualizarTela(command: AlterarTelaBannerCommand): Promise<boolean> {
        if (!command.id) {
            throw new Error('ID da tela é obrigatório para atualização');
        }
        return apiService.put<boolean>('/v1/banner/tela', command);
    }

    /**
     * Desvincula produtos de uma tela de banner
     * @param telaId - ID da tela
     * @param produtoIds - Array de IDs dos produtos a desvincular
     */
    async desvincularProdutos(telaId: number, produtoIds: number[]): Promise<boolean> {
        if (!telaId || telaId <= 0) {
            throw new Error('ID da tela é obrigatório e deve ser maior que zero');
        }
        if (!Array.isArray(produtoIds) || produtoIds.length === 0) {
            throw new Error('produtoIds deve ser um array não vazio');
        }
        return apiService.put<boolean>('/v1/banner/tela/desvincular', {
            id: telaId,
            produtoIds: produtoIds
        });
    }

    /**
     * Exclui uma tela de banner do usuário autenticado.
     * Para panfleto (tipo 1), remove antes a foto em tb_banner_tela_fotos
     * (FK fk_tb_banner_tela_fotos_cd_tela) e só então a tela.
     * @param telaId - ID da tela a ser excluída
     * @param tipo - Tipo da tela (1 = panfleto). Quando informado e panfleto, deleta a foto primeiro.
     */
    async deletarTela(telaId: number, tipo?: number): Promise<boolean> {
        // Panfleto: ordem obrigatória foto → tela por causa da FK
        if (tipo === 1) {
            try {
                await this.deletarFotoTela(telaId);
            } catch (error) {
                // Sem foto ou já removida: seguir com a exclusão da tela
                console.warn(
                    `[bannerService] Foto da tela panfleto ${telaId} não removida (pode não existir):`,
                    error
                );
            }
        }
        return apiService.delete<boolean>(`/v1/banner/tela/${telaId}`);
    }

    /**
     * Upload do vídeo da tela do banner
     * @param telaId - ID da tela
     * @param videoFile - Arquivo de vídeo
     */
    async uploadVideo(telaId: number, videoFile: File): Promise<void> {
        return apiService.uploadFile(
            `/v1/banner/tela/${telaId}/video`,
            videoFile,
            'Video'
        );
    }

    /**
     * Download do vídeo da tela do banner
     * @param telaId - ID da tela
     */
    async downloadVideo(telaId: number): Promise<Blob> {
        return apiService.downloadFile(`/v1/banner/tela/${telaId}/video`);
    }

    /**
     * Obtém a URL do vídeo da tela do banner
     * @param telaId - ID da tela
     */
    getVideoUrl(telaId: number): string {
        // Servidor de produção (comentado para usar localhost)
        // const baseUrl = process.env.REACT_APP_API_SERVER_1 || 'https://www.market.net.br/r';
        
        // Servidor localhost para desenvolvimento
        const baseUrl = process.env.REACT_APP_API_SERVER_1 || 'http://localhost:5000';
        const empresa = process.env.REACT_APP_EMPRESA;
        const token = apiService.getToken();
        return `${baseUrl}/${empresa}/v1/banner/tela/${telaId}/video?token=${token}`;
    }

    /**
     * Valida o código do totem e código da tela (linkar tela)
     * @param codigo - Código do totem (pode ser código falso de 5 caracteres ou código real)
     * @param codigoDisplay - Código da tela (codigoDisplay) OU cdTelevisao quando for televisão
     * @param lojaId - ID da loja do usuário logado
     * @param nomeSequencia - Nome da sequência (opcional)
     * @param isTelevisao - Flag indicando se é televisão (quando true, codigoDisplay contém cdTelevisao e é enviado no campo cdTelevisao)
     * @returns Token, dados do vínculo E o código real usado (codigoReal)
     */
    async validarCodigoTelevisao(
        codigo: string, 
        codigoDisplay: number | null, 
        lojaId: number, 
        nomeSequencia?: string,
        isTelevisao?: boolean
    ): Promise<{ token: string; codigoDisplay: number | null; cdMediaPlayerTelevisao: number; nomeSequencia?: string; cdTelevisao?: number; codigoReal: string }> {
        const codigoTrimmed = codigo.trim().toUpperCase();
        
        // O código já foi gerado na TV com empresa, agora apenas validar
        const codigoParaValidar = codigoTrimmed;
        
        const payload: any = {
            Codigo: codigoParaValidar,
            LojaId: lojaId
        };
        
        // Se for TELEVISÃO: enviar CdTelevisao, não enviar CodigoDisplay
        if (isTelevisao && codigoDisplay !== null && codigoDisplay !== undefined) {
            payload.CdTelevisao = codigoDisplay; // codigoDisplay contém o cdTelevisao quando isTelevisao=true
            // IMPORTANTE: Não enviar CodigoDisplay quando for televisão
            if (payload.CodigoDisplay) {
                delete payload.CodigoDisplay;
            }
        }
        // Se for TELA: enviar CodigoDisplay, não enviar CdTelevisao
        else if (codigoDisplay !== null && codigoDisplay !== undefined) {
            payload.CodigoDisplay = codigoDisplay;
            // IMPORTANTE: Não enviar CdTelevisao quando for tela
            if (payload.CdTelevisao) {
                delete payload.CdTelevisao;
            }
        }
        
        // Se for SEQUÊNCIA: enviar NomeSequencia, não enviar CodigoDisplay nem CdTelevisao
        if (nomeSequencia && nomeSequencia.trim() !== '' && !isTelevisao) {
            payload.NomeSequencia = nomeSequencia;
            // IMPORTANTE: Não enviar CodigoDisplay nem CdTelevisao quando for sequência
            if (payload.CodigoDisplay) {
                delete payload.CodigoDisplay;
            }
            if (payload.CdTelevisao) {
                delete payload.CdTelevisao;
            }
        }
        
        const response = await apiService.post<{ token: string; codigoDisplay: number | null; cdMediaPlayerTelevisao: number; nomeSequencia?: string; cdTelevisao?: number }>('/v1/banner/televisao/codigo/validar', payload);
        
        // Retornar resposta com código real usado (importante para atualizarCodigoDisplay)
        return {
            ...response,
            codigoReal: codigoParaValidar
        };
    }

    /**
     * Atualiza a relação entre código do totem e código da tela no banco de dados
     * @param cdMediaPlayerTelevisao - ID do media player retornado na validação
     * @param dsCodigo - Código do totem
     * @param cdCodigoDisplay - Código da tela
     */
    async atualizarCodigoDisplay(cdMediaPlayerTelevisao: number, dsCodigo: string, cdCodigoDisplay: number): Promise<void> {
        try {
            return await apiService.put<void>('/v1/banner/televisao/codigo/display', {
                CdMediaPlayerTelevisao: cdMediaPlayerTelevisao,
                DsCodigo: dsCodigo,
                CdCodigoDisplay: cdCodigoDisplay
            });
        } catch (error: any) {
            // Log detalhado do erro para debug
            console.error('Erro ao atualizar código display:', {
                endpoint: '/v1/banner/televisao/codigo/display',
                method: 'PUT',
                payload: {
                    CdMediaPlayerTelevisao: cdMediaPlayerTelevisao,
                    DsCodigo: dsCodigo,
                    CdCodigoDisplay: cdCodigoDisplay
                },
                error: error
            });
            throw error;
        }
    }

    /**
     * Busca o token e codigoDisplay salvos no cache pelo código gerado
     * GET direto na URL: /v1/banner/televisao/codigo/{codigo}/token
     * Em produção, inclui empresa na URL: /r/{empresa}/v1/banner/televisao/codigo/{codigo}/token
     * @param codigo - Código gerado na página de televisão
     * @param empresa - Nome da empresa (opcional, necessário apenas em produção)
     * @returns Token, codigoDisplay e nomeSequencia (se for sequência) se encontrado no cache, null caso contrário
     */
    async buscarTokenPorCodigo(codigo: string, empresa?: string): Promise<{ token: string; codigoDisplay: number | null; nomeSequencia?: string; cdTelevisao?: number } | null> {
        try {
            const codigoTrimmed = codigo.trim().toUpperCase();
            
            // Verificar se é produção (não localhost)
            const apiServer1 = process.env.REACT_APP_API_SERVER_1 || '';
            const isProduction = apiServer1.includes('market.net.br') || 
                                apiServer1.includes('market.srv.br');
            
            let fullUrl: string | null = null;
            if (isProduction && empresa) {
                // Em produção, construir URL completa com empresa
                let baseUrl = apiServer1;
                // Se a base já tem /r/empresa, remover a empresa
                if (baseUrl.match(/\/r\/[^/]+$/)) {
                    baseUrl = baseUrl.replace(/\/r\/[^/]+$/, '/r');
                }
                // Se não tem /r, adicionar
                if (!baseUrl.endsWith('/r')) {
                    baseUrl = baseUrl.endsWith('/') ? `${baseUrl}r` : `${baseUrl}/r`;
                }
                fullUrl = `${baseUrl}/${empresa.toLowerCase()}/v1/banner/televisao/codigo/${codigoTrimmed}/token`;
            }
            
            let response: { token: string; codigoDisplay: number | null; nomeSequencia?: string; cdTelevisao?: number };
            
            if (fullUrl) {
                // Produção: fazer fetch direto com URL completa
                const token = apiService.getToken();
                const headers: Record<string, string> = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                const fetchResponse = await fetch(fullUrl, {
                    method: 'GET',
                    headers
                });
                
                if (!fetchResponse.ok) {
                    if (fetchResponse.status === 404 || fetchResponse.status === 401) {
                        return null;
                    }
                    throw new Error(`Erro ${fetchResponse.status}: ${fetchResponse.statusText}`);
                }
                
                const responseText = await fetchResponse.text();
                if (!responseText || responseText.trim() === '') {
                    return null;
                }
                
                response = JSON.parse(responseText);
            } else {
                // Localhost: usar apiService normalmente
                response = await apiService.get<{ token: string; codigoDisplay: number | null; nomeSequencia?: string; cdTelevisao?: number }>(`/v1/banner/televisao/codigo/${codigoTrimmed}/token`);
            }
            
            if (response && response.token) {
                return response;
            }
            
            return null;
        } catch (error: any) {
            // Se retornar erro 404, 401 ou null, significa que o código não foi validado ainda ou expirou
            const errorMessage = error.message || '';
            if (
                errorMessage.includes('404') || 
                errorMessage.includes('401') ||
                errorMessage.includes('não encontrado') ||
                errorMessage.includes('Não autorizado') ||
                errorMessage.includes('null')
            ) {
                return null;
            }
            // Para outros erros, apenas logar e retornar null
            console.debug('Erro ao buscar token por código:', error);
            return null;
        }
    }
}

export const bannerService = new BannerService();
